import React, { type ComponentType, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Grid as GridBase,
  Typography as TypographyBase,
} from "@mui/material";

import EmailCampaignConfirmLoc from "./EmailCampaignConfirmLoc";
import RpMUIModal from "@/components/modals/RpMUIModal";
import { ROUTES } from "@/helpers/routes/routePaths";

type EmailCampaign = {
  name: string;
  [key: string]: unknown;
};

type LocationSelection = {
  [key: string]: unknown;
};

type EmailCampaignJoinConfirmationProps = {
  campaign: EmailCampaign;
  selections?: Record<string, LocationSelection>;
  onMakeChange?: () => void;
  onDeleteLoc?: (_locationId?: string | number) => void | Promise<void>;
};

const Grid = GridBase as ComponentType<any>;
const Typography = TypographyBase as ComponentType<any>;

function EmailCampaignJoinConfirmation({
  campaign,
  selections,
  onMakeChange,
  onDeleteLoc,
}: EmailCampaignJoinConfirmationProps) {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);

  const handleConfirm = useCallback(() => {
    setShowSuccess(true);
    setTimeout(() => {
      navigate(ROUTES.EMAIL_CAMPAIGNS, { replace: true });
    }, 3000);
  }, [navigate]);

  const renderNoLocation = useCallback(() => {
    if (!Object.keys(selections || {})?.length) {
      return (
        <div className="d-flex-col-center my-5">
          <Typography variant="p" className="fw-b mt-2 text-center">
            You haven't made selections for any location.
          </Typography>
          <Button
            variant="contained"
            onClick={onMakeChange}
            className="my-4 text-white"
            style={{ backgroundColor: "var(--status-warning)" }}
            disableElevation
          >
            Go back to campaign selections
          </Button>
        </div>
      );
    }
  }, [onMakeChange, selections]);

  const renderLocations = () => {
    if (!Object.keys(selections || {})?.length) {
      return null;
    }

    return (
      <>
        {Object.keys(selections).map((l) => (
          <EmailCampaignConfirmLoc
            key={l}
            data={selections[l]}
            onDeleteLoc={onDeleteLoc}
          />
        ))}

        <Card className="mt-4 bg-light" elevation={0}>
          <CardContent>
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="caption">
                  Once 'Confirm' is selected, you can't make changes, your
                  selections will be final for this campaign.
                </Typography>
              </Grid>
              <Grid
                item
                justifyContent="flex-end"
                size={{
                  xs: 12,
                  md: "auto",
                }}
              >
                <Grid container spacing={1} justifyContent="flex-end">
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={onMakeChange}
                    >
                      Make Changes
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleConfirm}
                    >
                      Confirm and Send Email
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </>
    );
  };
  return (
    <div>
      <Typography color="text.primary" variant="h5" component="h1">
        Confirmation
      </Typography>
      <Typography variant="caption" className="mb-5">
        Review your selections and make changes if required before you confirm.
      </Typography>

      {renderLocations()}
      {renderNoLocation()}

      <RpMUIModal open={showSuccess} persists maxWidth="sm">
        <div className="text-center py-4">
          <img
            alt="Scheduled successfully"
            src="/img/icons/checked.png"
            className="img-fluid mb-3 mx-auto"
          />
          <Typography variant="h6" gutterBottom>
            {`${campaign.name} Email Campaign`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Scheduled Successfully
          </Typography>
        </div>
      </RpMUIModal>
    </div>
  );
}

export default EmailCampaignJoinConfirmation;
